import {concat, defer, EMPTY, Observable, OperatorFunction} from 'rxjs';
import {mergeMap} from 'rxjs/operators';

/**
 * RxJs pipeable operator.
 * Similar operators: {@link switchMap}, {@link exhaustMap}, {@link throttle}.
 * Projects input event to a new observable using process callback.
 * Waits for the previous projection to complete, during this waiting skips all intermediate events,
 * but the most recent one.
 * Marble diagram:
 * Input:  A--B--C--D------
 * Output: aaaa-bbbb-dddd--
 * @param {(event: T) => Observable<R>} process
 * @returns {(source: Observable<T>) => Observable<R>}
 */
export const waitPreviousProcessLast = <T, R>(process: (event: T) => Observable<R>): OperatorFunction<T, R> =>
    event$ => defer(() => {
        const queue: T[] = [];
        let isProcessing: boolean = false;

        const addToQueue = (event: T): Observable<never> => {
            queue.pop();
            queue.push(event);

            return EMPTY;
        };

        const processThisEvent = (event: T): Observable<R> => {
            isProcessing = true;

            return concat(
                process(event),
                processNextEvent$
            );
        };

        const processNextEvent$ = defer<Observable<R | never>>(() =>
            queue.length
                ? processThisEvent(queue.pop())
                : defer(() => {
                    isProcessing = false;

                    return EMPTY;
                })
        );

        return event$.pipe(
            mergeMap(event => isProcessing
                ? addToQueue(event)
                : processThisEvent(event)
            )
        );
    });
